import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, NgModule, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TranslateService } from '@codeandweb/ngx-translate';
import { Datasource, UiScrollModule } from 'ngx-ui-scroll';

import { HookResult, StateService, Transition, UiOnExit, UIRouterModule } from '@uirouter/angular';

import { Observable } from 'rxjs';

import { CalendarModule } from '@app/primeng-overrides/calendar';
import { MenuModule } from '@app/primeng-overrides/menu';
import { ConfirmationService } from 'primeng/api';
import { DataViewModule } from 'primeng/dataview';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { PanelModule } from 'primeng/panel';
import { ScrollTopModule } from 'primeng/scrolltop';
import { VirtualScrollerModule } from 'primeng/virtualscroller';

import { EventManagerService, IEventListener } from '@app/_global/event-manager.service';
import { Abonne } from '@app/abonne/abonne.model';
import { AbonneService } from '@app/abonne/abonne.service';
import { HTMLEditorModule } from '@app/components/html-editor';
import { Rubrique } from '@app/nomenclature/nomenclature.model';
import { NomenclaturePipe } from '@app/nomenclature/nomenclature.pipe';
import { NomenclatureService } from '@app/nomenclature/nomenclature.service';
import { SiteSelectorModule } from '@app/site/site-selector';
import { Site } from '@app/site/site.model';
import { SiteService } from '@app/site/site.service';
import { EvaluationSelectorModule } from '@app/texte/evaluation-selector';
import { HistoriqueConformiteArticleDialogComponent } from '@app/texte/historique-conformite-article-dialog';
import { HistoriqueConformiteTexteDialogComponent } from '@app/texte/historique-conformite-texte-dialog';
import { StatutRecolementSelectorModule } from '@app/texte/statut-recolement-selector';
import { TexteModule } from '@app/texte/texte';
import { Article, ArticleEvaluation, EvaluationArticle, EvaluationTexte, ModificationsArticle, StatutRecolement, Texte, TexteEvaluation } from '@app/texte/texte.model';
import { Utilisateur } from '@app/utilisateur/utilisateur.model';
import { GlobalModule } from '@global/global.module';
import { CommentaireTexteDialogComponent } from '../commentaire-texte-dialog';
import { FutureVersionArticleComponent } from '../future-version-article-dialog';
import { LinkedTextesDialogComponent } from '../linked-textes-dialog';
import { OperationsSectionModule } from '../operations-section/operations-section';

import { normalizeStringForSearch, simpleComparison, startOfDay, uid } from '@helpers/utils';

@Component({
	selector: 'conformite-texte',
	templateUrl: './conformite-texte.html'
})
export class ConformiteTexteComponent implements IEventListener, OnInit, OnDestroy, UiOnExit {

	private _uuid: string = uid();
	get uuid(): string { return this._uuid; }

	@ViewChild('articleScroller', { static: true, read: ElementRef }) articleScoller: ElementRef<HTMLDivElement>;
	@ViewChild('rubriques', { static: true, read: ElementRef }) rubriquesDiv: ElementRef<HTMLDivElement>;
	rubriquesExpanded?: boolean = false; // undefined si l'expansion/réduction n'est pas disponible

	abonne_courant: Abonne;

	@Input() me: Utilisateur;
	@Input() texte: Texte;
	@Input() texteEvaluation: TexteEvaluation;
	@Input() sites: Site[];

	pristineTexteEvaluation: TexteEvaluation;
	evaluationsArticlesById: Record<number, EvaluationArticle[]> = {};
	pristineEvaluationsArticlesById: Record<number, EvaluationArticle[]> = {};

	effectiveStatutTexteBySite: Record<number, StatutRecolement> = {};
	forcedRecolementArticlesBySiteId: Record<number, boolean> = {}; // empêche l'utilisateur de changer le statut
	excludedStatuses: StatutRecolement[]; // statuts qui ne sont pas utilisables en écriture
	bulkReplacementExcludedStatuses: StatutRecolement[];

	bulkStatutsReplacementSiteId?: number
	bulkStatutsReplacement?: StatutRecolement

	articlesEvaluations: ArticleEvaluation[] // articles affichés

	today: Date = startOfDay(new Date());
	canEditEvaluationsOfAtLeastOneSite = false;
	canEditEvaluationsOfSite: Record<number /*sit_id*/, boolean> = {};
	showTexteDetails = false;
	hasChanges = false;
	skipExitCheck = false;

	search: string | null = null;
	sta_code_article?: string;
	expandedArticles: Record<number, boolean> = {};
	hasArticlesWithStatusUnknown = false;
	modifsArticles: Record<number/*art_id*/, ModificationsArticle> = {};

	datasource = this.createScrollerDataSource();
	filteredArticlesCache: {search?: string, articles: Article[]} = {search: '', articles: []}

	constructor(
		private stateService: StateService,
		private confirmationService: ConfirmationService,
		private eventManager: EventManagerService,
		private translate: TranslateService,
		private dialogService: DialogService,
		private abonneService: AbonneService,
		private siteService: SiteService,
		private nomenclaturePipe: NomenclaturePipe,
		private nomenclatureService: NomenclatureService
	) {

	}

	ngOnInit(): void {
		this.nomenclatureService.getModificationsArticles(this.texte.txt_id)
		.subscribe((modifs: ModificationsArticle[]) => {
			for (let modif of modifs) {
				this.modifsArticles[modif.art_id] = modif;
			}
		});
		
		this.abonne_courant = this.abonneService.currentAbonneValue!;

		if (this.texte.txt_abrogation || !this.texteEvaluation.evaluable) {
			// On ne peut rien éditer
			for (let site of this.sites) {
				this.canEditEvaluationsOfSite[site.sit_id] = false;
			}
			this.canEditEvaluationsOfAtLeastOneSite = false;
		}
		else {
			for (let site of this.sites) {
				this.canEditEvaluationsOfSite[site.sit_id] = false;

				if (site.nac_id == 2 || this.me.uti_administrateur) {
					this.canEditEvaluationsOfAtLeastOneSite = true;
					this.canEditEvaluationsOfSite[site.sit_id] = true;
				}
			}
		}

		// On trie les rubriques par ordre alphabétique sur leur nom entier
		this.texte = structuredClone(this.texte);
		this.texte.rubriques = this.texte.rubriques.sort((rubA: Rubrique, rubB: Rubrique) =>  {
			let nomenclatureA = this.nomenclaturePipe.transform(rubA);
			let nomenclatureB = this.nomenclaturePipe.transform(rubB);
			return nomenclatureA < nomenclatureB ? - 1 : 1;
		});

		let texteEvaluation = this.texteEvaluation;
		this.pristineTexteEvaluation = structuredClone(this.texteEvaluation);
		this.articlesEvaluations = [];

		if (this.sites.length == 1) {
			this.bulkStatutsReplacementSiteId = this.sites[0].sit_id;
		}

		for (let articleEvaluation of texteEvaluation.articles) {
			let evaluations = articleEvaluation.conformite;
			this.articlesEvaluations.push({
				...articleEvaluation,
				conformite: evaluations
			});
			this.evaluationsArticlesById[articleEvaluation.art_id] = evaluations;
			if (evaluations.some((eva: EvaluationArticle) => eva.sta_code == 'inc')) {
				this.hasArticlesWithStatusUnknown = true;
			}
		}

		this.pristineEvaluationsArticlesById = structuredClone(this.evaluationsArticlesById);
		this.pristineTexteEvaluation = structuredClone(this.texteEvaluation);

		this.excludedStatuses = this.me.uti_administrateur ? [] : ['mod']
		this.bulkReplacementExcludedStatuses = [...this.excludedStatuses, 'inc']

		// On force le statut des articles abrogés à informatif et on affiche ni le statut ni l'évaluation
		for (let articleEvaluation of texteEvaluation.articles) {
			let evaluations = articleEvaluation.conformite;

			let fullArticle = this.texte.articles.find((fullArticle: Article) => fullArticle.art_id == articleEvaluation.art_id);

			if (fullArticle?.art_abrogation) {
				for (let evaluation of evaluations) {
					evaluation.sta_code = 'inf';
					evaluation.eva_code = 'nap';
				}
			}
		}

		for (let evaluation of this.texteEvaluation.conformite) {
			this.effectiveStatutTexteBySite[evaluation.sit_id] = evaluation.sta_code;
		}

		// On agrandit tous les articles courts par défaut et on arrange les art_ordre
		this.texte.articles.forEach((article: Article, index: number) => {
			if (article.art_corps && article.art_corps.length < 100) {
				this.expandedArticles[article.art_id] = true;
			}
			article.art_ordre = index;
		});

		this.checkHasChanges();

		this.eventManager.registerEvent('scrollTop', this, (args: any) => {
			this.articleScoller.nativeElement.scrollTop = 0;
		});

		this.eventManager.emit('define_scroll_threshold', 1);
	}

	ngAfterViewInit(): void {
		if (this.rubriquesDiv.nativeElement.clientHeight < 120) {
			this.rubriquesDiv.nativeElement.classList.add('expanded');
		}
		else {
			this.rubriquesExpanded = false;
		}
	}

	ngOnDestroy(): void {
		this.eventManager.unregisterEvent('scrollTop', this);
		this.eventManager.emit('define_scroll_threshold', 'reset');
	}

	expandRubriques(event: MouseEvent) {
		if (this.rubriquesExpanded === undefined || this.rubriquesExpanded) {
			return;
		}
		this.rubriquesExpanded = true;
		this.rubriquesDiv.nativeElement.classList.add('expanded');

		event.stopImmediatePropagation();
	}

	collapseRubriques(event: MouseEvent) {
		if (this.rubriquesExpanded === undefined || !this.rubriquesExpanded) {
			return;
		}
		this.rubriquesExpanded = false;
		this.rubriquesDiv.nativeElement.classList.remove('expanded');

		event.stopImmediatePropagation();
	}

	createScrollerDataSource() {
		return new Datasource<Article>({
			settings: {
				startIndex: 0, // Par défaut startIndex est à 1
				padding: 10 // On évite la majorité des incrémentation brutales du défilement
			},
			get: (index: number, count: number, success: Function) => {
				let shownArticleIds = this.articlesEvaluations.map(article => article.art_id);
				let articles = this.texte.articles.filter(article => shownArticleIds.includes(article.art_id));
				let search = this.search;

				if (this.sta_code_article) {
					articles = articles.filter((article: Article) => {
						let articleConformite = this.articlesEvaluations.find(art => art.art_id == article.art_id)

						if (articleConformite == undefined) {
							return false;
						}


						return articleConformite.conformite.some((evaluation: EvaluationArticle) => evaluation.sta_code == this.sta_code_article);
					});
				}

				if (search && search?.trim() != '') {
					search = search.trim();
					if (this.filteredArticlesCache.search == search) {
						articles = this.filteredArticlesCache.articles;
					}
					else {
						const regex = new RegExp(normalizeStringForSearch(search)!, 'i');

						articles = articles.filter((article: Article) => {
							let normalizedTitle = normalizeStringForSearch(article.art_titre ?? '');
							let normalizedBodyPlaintext = normalizeStringForSearch(article.art_corps_plaintext ?? '');

							return normalizedTitle.match(regex) || normalizedBodyPlaintext.match(regex);
						});

						this.filteredArticlesCache = {search: search, articles: articles};
					}
				}

				let result = articles.slice(index, index + count);
				success(result);
			}
		});
	}

	refreshArticleList() {
		return this.datasource.adapter.reset();
	}

	onRecolementTexteChange(evaluationTexte: EvaluationTexte) {

		let sit_id = evaluationTexte.sit_id;
		let articles = this.articlesEvaluations;
		let pristineEvaluation = this.pristineTexteEvaluation.conformite.find((eva: EvaluationTexte) => eva.sit_id == evaluationTexte.sit_id);

		if (articles.length == 1 && pristineEvaluation && pristineEvaluation.sta_code == 'inc') {
			for (let eva of articles[0].conformite) {
				if (eva.sit_id == evaluationTexte.sit_id) {
					eva.sta_code = evaluationTexte.sta_code;
					this.checkHasChanges();
					return;
				}
			}
		}

		switch (evaluationTexte.sta_code) {
			case 'inf':
				// On met tous les articles à informatif sauf ceux en non-applicable ou modificatifs qu'on conserve
				for (let article of articles) {
					for (let eva of article.conformite) {
						if (eva.sit_id == sit_id && eva.sta_code != 'nap' && !article.art_modificatif) {
							eva.sta_code = 'inf';
						}
					}
				}
				this.effectiveStatutTexteBySite[sit_id] = evaluationTexte.sta_code;
				this.checkHasChanges();
				break;
			case 'nap':
				// On met tous les articles en non-applicable sauf ceux modificatifs qu'on conserve
				for (let article of articles) {
					for (let eva of article.conformite) {
						if (eva.sit_id == sit_id && !article.art_modificatif) {
							eva.sta_code = 'nap';
						}
					}
				}
				this.effectiveStatutTexteBySite[sit_id] = evaluationTexte.sta_code;
				this.checkHasChanges();
				break;

				// Ancienne logique, on faisait plus de vérification et on demandait l'accord de l'utilisateur avant de faire un gros changement

				// // Si tous les récolements du site ont le statut 'Inconnu' ou 'Non applicable', on les passe à 'Non applicable'
				// if (articles.every((art: ArticleEvaluation) => art.conformite.every(eva => eva.sit_id != sit_id || eva.sta_code == 'inc' || eva.sta_code == 'nap'))) {
				// 	this.forceStatusOfAllArticlesForSite('nap', sit_id);
				// 	this.effectiveStatutTexteBySite[sit_id] = evaluationTexte.sta_code;
				// 	this.checkHasChanges();
				// }
				// // Sinon on demande confirmation pour les passer à 'Non applicable'
				// else {
				// 	this.confirmationService.confirm({
				// 		defaultFocus: 'reject',
				// 		message: this.translate.instant('recolements.non_applicable_confirmation_message'),
				// 		accept: () => {
				// 			this.forceStatusOfAllArticlesForSite('nap', sit_id);
				// 			this.effectiveStatutTexteBySite[sit_id] = evaluationTexte.sta_code;
				// 			this.checkHasChanges();
				// 		},
				// 		reject: () => {
				// 			// On revient au statut précédent
				// 			evaluationTexte.sta_code = this.effectiveStatutTexteBySite[sit_id];
				// 			this.checkHasChanges();
				// 		}
				// 	});
				// }
				// break;
			default:
				this.effectiveStatutTexteBySite[sit_id] = evaluationTexte.sta_code;
				this.checkHasChanges();
		}
	}

	onRecolementArticleChange(evaluationArticle: EvaluationArticle) {
		let sit_id = evaluationArticle.sit_id;
		let evaluations = this.texteEvaluation.articles
			.map(article => article.conformite)
			.flat()
			.filter(recArticle => recArticle.sit_id == sit_id);

		this.forcedRecolementArticlesBySiteId[sit_id] = false; // On met ensuite la valeur à vrai selon le statut de l'article

		switch (evaluationArticle.sta_code) {
		case 'app':
			// Si au moins un article est applicable on met le texte en applicable
			let recolementTexte = this.texteEvaluation.conformite.find(eva => eva.sit_id == sit_id);
			recolementTexte!.sta_code = 'app';
			this.effectiveStatutTexteBySite[sit_id] = 'app';
			this.forcedRecolementArticlesBySiteId[sit_id] = true;
			break;
		case 'nap':
			// Si tous les articles sont non-applicables on met le texte en non-applicable
			if (evaluations.every((eva: EvaluationArticle) => eva.sta_code == 'nap')) {
				let recolementTexte = this.texteEvaluation.conformite.find(eva => eva.sit_id == sit_id);
				recolementTexte!.sta_code = 'nap';
				this.effectiveStatutTexteBySite[sit_id] = 'nap';
				this.forcedRecolementArticlesBySiteId[sit_id] = true;
			}
			break;
		case 'inf':
			// Si tous les articles sont informatifs on met le texte en informatif
			if (evaluations.every((eva: EvaluationArticle) => eva.sta_code == 'inf')) {
				let recolementTexte = this.texteEvaluation.conformite.find(eva => eva.sit_id == sit_id);
				recolementTexte!.sta_code = 'inf';
				this.effectiveStatutTexteBySite[sit_id] = 'inf';
				this.forcedRecolementArticlesBySiteId[sit_id] = true;
			}
			break;
		}

		this.checkHasChanges();
	}

	
	replaceUnknownStatusInBulk() {
		if (this.bulkStatutsReplacement == undefined || this.bulkStatutsReplacementSiteId == undefined) {
			return;
		}

		let articles = this.articlesEvaluations;

		let anyEncounteredRecolementArticle;

		for (let article of articles) {
			for (let eva of article.conformite) {
				if (eva.sit_id == this.bulkStatutsReplacementSiteId && eva.sta_code == 'inc') {
					eva.sta_code = this.bulkStatutsReplacement;

					if (anyEncounteredRecolementArticle == undefined) {
						anyEncounteredRecolementArticle = eva;
					}
				}
			}
		}

		if (anyEncounteredRecolementArticle) {
			this.onRecolementArticleChange(anyEncounteredRecolementArticle);
		}
		this.checkHasChanges();
	}

	submit() {
		let texte = structuredClone(this.texteEvaluation);

		this.siteService.updateSomeEvaluationsTextes(this.abonne_courant.abo_id, this.sites.map(site => site.sit_id), [texte])
		.subscribe(() => {
			this.eventManager.emit('toast', {severity: 'success', summary: this.translate.instant('assessments.updated_success')});
			this.skipExitCheck = true;
			this.stateService.go('^');
		});
	}

	checkHasChanges() {
		this.hasChanges =
			!simpleComparison(this.texteEvaluation.conformite, this.pristineTexteEvaluation.conformite)
			|| !simpleComparison(this.evaluationsArticlesById, this.pristineEvaluationsArticlesById);

		this.hasArticlesWithStatusUnknown = false;

		for (let evaluations of Object.values(this.evaluationsArticlesById)) {
			if (evaluations.some((eva: EvaluationArticle) => eva.sta_code == 'inc')) {
				this.hasArticlesWithStatusUnknown = true;
				break;
			}
		}
	}

	exit() {
		this.skipExitCheck = true;
		this.stateService.go('^');
	}

	showTexteCommentaryDialog() {
		this.dialogService.open(CommentaireTexteDialogComponent, {
			header: this.translate.instant('textes.commentaire_of_text', {text: this.texte.txt_reference}),
			styleClass: 'max-w-120rem p-dialog-high-height',
			data: {
				texte: this.texte,
			}
		});
	}

	showTextHistoryDialog() {
		this.dialogService.open(HistoriqueConformiteTexteDialogComponent, {
			header: this.translate.instant('assessments.history_modal_title', {text: this.texte.txt_reference}),
			width: '85rem',
			data: {
				txt_id: this.texte.txt_id,
				sit_ids: this.sites.map(site => site.sit_id)
			}
		});
	}

	showTargetedTextesList() {
		this.dialogService.open(LinkedTextesDialogComponent, {
			header: this.translate.instant('textes.targeted_textes_modal_title', {text: this.texte.txt_reference}),
			width: '80%',
			data: {
				liste: this.texte.textes_modifies,
				mode: 'targeted'
			}
		});
	}

	showModifyingTextesList() {
		this.dialogService.open(LinkedTextesDialogComponent, {
			header: this.translate.instant('textes.modifying_textes_button_label', {text: this.texte.txt_reference}),
			width: '80%',
			data: {
				liste: this.texte.textes_modificatifs,
				mode: 'modifying'
			}
		});
	}
	
	showArticleHistoryDialog(article: Article) {
		this.dialogService.open(HistoriqueConformiteArticleDialogComponent, {
			header: this.translate.instant('assessments.article_history_modal_title', {article: article.art_titre}),
			width: '85rem',
			data: {
				art_id: article.art_id,
				sit_ids: this.sites.map(site => site.sit_id)
			}
		});
	}


	showArticleModifyingTextesList(article: Article, modifs: ModificationsArticle) {
		this.dialogService.open(LinkedTextesDialogComponent, {
			header: this.translate.instant('textes.modifying_textes_for_article', {article: article.art_titre}),
			width: '80%',
			data: {
				liste: modifs.textes_modificatifs,
				mode: 'modifying',
				me: this.me
			},
		});
	}

	showArticleFutureVersions(article: Article, modifs: ModificationsArticle) {
		this.dialogService.open(FutureVersionArticleComponent, {
			header: this.translate.instant('textes.future_versions_of_article', {article: article.art_titre}),
			width: '80%',
			data: {
				article: article,
				future_versions: modifs.versions_article
			}
		});
	}
	

	uiCanExit(newTransition?: Transition): HookResult {
		if (!this.skipExitCheck && this.hasChanges) {
			return new Observable<any>(sub => {
				this.confirmationService.confirm({
					defaultFocus: 'reject',
					header: this.translate.instant('common.leaving_unsaved_form_confirm_header'),
					message: this.translate.instant('common.leaving_unsaved_form_confirm_message'),
					accept: () => {
						sub.next(true);
						sub.complete();
					},
					reject: () => {
						sub.next(false);
						sub.complete();
					}
				});
			}).toPromise();
		}
	}

}

@NgModule({
	imports: [
		CommonModule,
		GlobalModule,
		UIRouterModule,
		PanelModule,
		FormsModule,
		MenuModule,
		PanelModule,
		DividerModule,
		InputTextareaModule,
		TexteModule,
		StatutRecolementSelectorModule,
		HTMLEditorModule,
		VirtualScrollerModule,
		DataViewModule,
		UiScrollModule,
		EvaluationSelectorModule,
		SiteSelectorModule,
		CalendarModule,
		ScrollTopModule,
		OperationsSectionModule
	],
	exports: [ConformiteTexteComponent],
	declarations: [ConformiteTexteComponent]
})
export class ConformiteTexteModule { }
